import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import type { ConversationTask, ConversationTaskStatus } from "@rakazo/contracts";
import { Button } from "@rakazo/ui-web";
import { Check, Circle, CircleAlert, Eye, Hand, LoaderCircle, Pause, X } from "lucide-react";
import { useState } from "react";
import { rpc } from "../lib/rpc";
import { errorText } from "../lib/user-error";

export function taskStatusLabel(status: ConversationTaskStatus): string {
  switch (status) {
    case "working":
      return t`Working`;
    case "queued":
      return t`Queued`;
    case "needs_input":
      return t`Needs you`;
    case "review":
      return t`Ready for review`;
    case "done":
      return t`Done`;
    case "paused":
      return t`Paused`;
    case "failed":
      return t`Failed`;
    case "cancelled":
      return t`Cancelled`;
  }
}
export function taskStatusTone(status: ConversationTaskStatus): string {
  if (status === "working") return "text-status-working";
  if (status === "needs_input") return "text-warning";
  if (status === "done" || status === "review") return "text-success";
  if (status === "failed") return "text-destructive";
  return "text-muted-foreground";
}
export function TaskStatusIcon({ status }: { status: ConversationTaskStatus }) {
  const Icon = {
    working: LoaderCircle,
    queued: Circle,
    needs_input: Hand,
    review: Eye,
    done: Check,
    paused: Pause,
    failed: CircleAlert,
    cancelled: X,
  }[status];
  return (
    <Icon
      aria-hidden="true"
      size={14}
      strokeWidth={1.8}
      className={status === "working" ? "motion-safe:animate-spin" : undefined}
    />
  );
}

/** Compact attribution, with task details revealed on demand rather than another chat card. */
export function ConversationTaskRows({
  task,
  onRefresh,
}: {
  task: ConversationTask;
  onRefresh: () => Promise<void>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function accept() {
    setBusy(true);
    setError(null);
    try {
      await rpc.runs.review({ runId: task.runId });
      await onRefresh();
    } catch (err) {
      setError(errorText(err, t`Could not update task`));
    } finally {
      setBusy(false);
    }
  }
  const steps = expanded
    ? task.steps
    : task.steps.filter((step) => step.status !== "pending").slice(-3);
  return (
    <div
      data-testid="conversation-task"
      data-task-status={task.status}
      className="ai-task-enter mb-2 max-w-xl text-[12px]"
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        className="flex max-w-full items-center gap-2 rounded-md py-1 text-left hover:bg-muted/50 active:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-safe:transition-colors motion-safe:duration-150"
      >
        <span
          className={`grid size-6 shrink-0 place-items-center rounded-md bg-current/10 ${taskStatusTone(task.status)}`}
        >
          <span key={task.status} className="ai-task-status">
            <TaskStatusIcon status={task.status} />
          </span>
        </span>
        <span className="truncate font-medium text-foreground/90">{task.title}</span>
        <span className={`shrink-0 text-[11px] ${taskStatusTone(task.status)}`}>
          {taskStatusLabel(task.status)}
        </span>
      </button>
      {steps.length ? (
        <ol className="ms-3 space-y-1 border-s border-border pb-1 ps-4 pt-1">
          {steps.map((step) => {
            const status =
              step.status === "done" ? "done" : step.status === "pending" ? "queued" : task.status;
            return (
              <li
                key={step.id}
                className="ai-task-enter flex items-center gap-2 text-muted-foreground"
              >
                <span key={status} className={`ai-task-status ${taskStatusTone(status)}`}>
                  <TaskStatusIcon status={status} />
                </span>
                <span>{step.title}</span>
              </li>
            );
          })}
        </ol>
      ) : null}
      {expanded ? (
        <div className="ai-task-enter ms-3 border-s border-border py-2 ps-4">
          {task.summary ? (
            <p className="whitespace-pre-wrap text-muted-foreground">{task.summary}</p>
          ) : null}
          {task.status === "review" ? (
            <Button
              size="xs"
              variant="secondary"
              className="mt-2"
              disabled={busy}
              onClick={() => void accept()}
            >
              <Trans>Mark reviewed</Trans>
            </Button>
          ) : null}
          {error ? (
            <p role="alert" className="mt-2 text-destructive">
              {error}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
